import { Component, Input, OnInit, Optional } from "@angular/core";
import { NgbActiveModal, NgbDate } from "@ng-bootstrap/ng-bootstrap";
import { CartService } from "../../cart.service";
import { applyAssociationWeeklyPrices, Attachment, InventoryItem, InventoryService } from "../../../inventory/inventory";
import { AzureSearchRequest, AzureSearchService } from "../../../azure-search/azure-search";
import { Cart, CartItem, DeliveryOptions } from "../../models/cart.model";
import { Charge } from "../../models/charge.model";
import { Address, AuthenticationService, DateRange, LocalStorageService, Location, LogService } from "../../../core/core";
import { TransportationInfo } from "../../../transportation/transportation";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { User } from "oidc-client";
import { Account, AccountService } from "../../../account/account";
import { OpenAIService } from "../../../rubbl-shared/services/open-ai.service";
import { of, forkJoin } from "rxjs";
import { catchError } from "rxjs/operators";
import { CouponService } from "../../services/coupon.service";
import { SearchResultsService } from "../../../search-results/services/search-results.service";

@Component({
  selector: "common-machine-card-add-to-cart",
  templateUrl: "./machine-card-add-to-cart.component.html",
  styleUrls: ["./machine-card-add-to-cart.component.scss"],
})
export class MachineCardAddToCartComponent implements OnInit {
  @Input() machine: InventoryItem;
  @Input() machineInCart: boolean;
  @Input() inCheckout: boolean;

  cachedTransportationEstimateSummary: any;
  /** Empty until Azure Search returns — keeps the dates-step picker visible immediately. */
  attachmentsFromIndex: Attachment[] = [];
  counter: number = 0;

  steps: any[];

  startDateStep = {
    name: "startDate",
    header: "Start Date, End Date and Delivery Address",
    buttonText: "",
    cancelButtonText: "Cancel",
    buttonMethod: () => this.startDateAndDurationCompleted(),
    buttonDisabled: () => {
      const missingAddress = !this.inCheckout && !this.hasValidDeliveryLocation(this.deliveryAddress);
      return this.startDate == null || this.endDate == null || missingAddress;
    },
  };

  // Retained for the in-checkout edit flow (which still renders this step) and so the
  // template's *ngFor over `steps` continues to compile; never reached from the normal
  // add-to-cart flow because we now skip straight from the date step to /cart.
  addToCartStep = {
    name: "addToCart",
    header: "Rental details",
    buttonText: "Add to Cart",
    cancelButtonText: "Cancel",
    buttonMethod: () => this.addToCartCompleted(),
    buttonDisabled: () => {
      return this.isDenied;
    },
  };

  startDate: NgbDate;
  endDate: NgbDate;
  duration: number = 0;
  zipcode: string;
  deliveryAddress: Location;
  invalidDeliveryAddress: boolean;
  cartItem: CartItem;
  loading: boolean;
  rpoInterested: boolean;
  invalidZipCode: boolean;
  deliveryOption: string = DeliveryOptions.Delivery;
  appliedCouponCode: string = "";
  validatedCouponCode: string;
  couponCode: string = "";
  couponLoading: boolean = false;
  couponError: string = "";

  user: User;
  userAccount: Account;
  isDenied: boolean;

  /**
   * After a successful add (or edit outside the checkout review flow) we navigate the user
   * straight to /cart instead of showing an intermediate "View cart / Continue shopping" modal.
   */
  get navigateToCartAfterSave(): boolean {
    return !this.inCheckout;
  }

  constructor(
    public activeModal: NgbActiveModal,
    private cartService: CartService,
    private azureSearchService: AzureSearchService,
    private logService: LogService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    private inventoryService: InventoryService,
    private accountService: AccountService,
    private openAIService: OpenAIService,
    private couponService: CouponService,
    @Optional() private searchResultsService?: SearchResultsService,
    @Optional() private localStorageService?: LocalStorageService,
  ) { }

  ngOnInit(): void {
    this.user = this.authenticationService.getUserNonAsync();
    if (this.user) {
      this.accountService.get(this.user.profile.account_id).subscribe(account => {
        this.userAccount = account;
        this.isDenied = this.userAccount.verificationStatus == "Denied";
      });
    }

    // need to filter out unavailable attachments
    // Keep available/onboarding attachments; also keep any attachment already referenced by the
    // existing cart item so edit flows can still display (and let the user remove) attachments
    // that have since become unavailable.
    const cartItemAttachmentIds = (this.cartItem?.attachments || []).map(a => a.childItemId);
    this.machine.relatedAttachments = MachineCardAddToCartComponent.filterAttachmentsByAvailability(
      this.machine.relatedAttachments,
      cartItemAttachmentIds,
    );

    // we need to set all attachments for available so the JSON serialization of cartitemstatus works. cartitemstatus only has 'available' and 'notavailable'....
    this.machine.relatedAttachments.forEach(a => {
      a.status = "Available";
    });
    if (this.inCheckout) {
      this.startDateStep.header = "Rental Dates";
      this.startDateStep.buttonText = "Save";
      this.addToCartStep.buttonText = "Save";
    } else if (this.machineInCart) {
      this.startDateStep.buttonText = "Edit";
      this.addToCartStep.buttonText = "Update Cart";
    } else {
      this.startDateStep.buttonText = "See Total";
      this.addToCartStep.buttonText = "Add to Cart";
    }

    // Always load existing delivery option and coupon code from cart if they exist.
    const cart = this.cartService.cart;
    if (cart.deliveryOption) {
      this.deliveryOption = cart.deliveryOption;
    }
    if (cart.couponCode) {
      this.appliedCouponCode = cart.couponCode;
      this.validatedCouponCode = cart.couponCode;
    }

    // if machine is in cart, it means we are editing!
    if (this.machineInCart) {
      this.cachedTransportationEstimateSummary = this.cartItem?.transportationEstimateSummary;

      // we need to grab the cart item so we can set start date, end date and duration from the cart
      const cartItem = this.cartService.getItemByInventoryItemId(this.machine.id);
      this.startDate = DateRange.createNgbDateFromString(cartItem.beginDate);
      this.duration = cartItem.duration;
      this.endDate = this.resolveEndDate(cartItem.beginDate, cartItem.endDate, cartItem.duration);

      this.deliveryAddress = this.resolveEditPrefillLocation(cartItem, cart);
      this.zipcode = this.deliveryPostalCode;

      // if the cart item has attachments we need to go through and set the machine attachments to be selected and/or installed

      if (cartItem.attachments) {
        for (const cartItemAttachment of cartItem.attachments) {
          let correspondingAttachmentOnMachine = this.machine.relatedAttachments.find(d => d.inventoryItemId == cartItemAttachment.childItemId);
          if (correspondingAttachmentOnMachine) {
            correspondingAttachmentOnMachine.install = cartItemAttachment.install;
            correspondingAttachmentOnMachine.selected = true;
            correspondingAttachmentOnMachine.cost = cartItemAttachment.price;
            correspondingAttachmentOnMachine.price = cartItemAttachment.price;
            // Keep the estimate API's weekly rate so pills show the exact weekly price on edit.
            correspondingAttachmentOnMachine.weeklyPrice = cartItemAttachment.weeklyPrice;
            correspondingAttachmentOnMachine.childItemId = cartItemAttachment.childItemId;
            correspondingAttachmentOnMachine.accountId =
              cartItemAttachment.accountId || correspondingAttachmentOnMachine.accountId || this.machine.accountId;
          }
        }
      }
    } else {
      if (cart.items.length != 0) {
        const lastItem = cart.items[cart.items.length - 1];
        this.startDate = DateRange.createNgbDateFromString(lastItem.beginDate);
        this.duration = lastItem.duration;
        this.endDate = this.resolveEndDate(lastItem.beginDate, lastItem.endDate, lastItem.duration);
        this.deliveryAddress = this.resolveNewAddPrefillLocation(lastItem, cart);
        this.zipcode = this.deliveryPostalCode;
      }
    }

    // Dates is always the first (and primary) step. Attachments are selected on the
    // dates screen under transportation — no separate wizard step.
    this.steps = [this.startDateStep, this.addToCartStep];
    this.logService.sendGoogleEcommerceView("add_to_cart_dates_modal_view", [this.machine]);

    if (this.machine.relatedAttachments.length) {
      const attachmentSearch = new AzureSearchRequest();
      attachmentSearch.filter = `search.in(id, '${this.machine.relatedAttachments.map(d => d.inventoryItemId).join(",")}')`;
      this.azureSearchService.search<Attachment>(attachmentSearch, "attachments").subscribe(result => {
        this.attachmentsFromIndex = result.value;
      });

      // Search relatedAttachments only have monthly `cost`. Hydrate owner-set weekly
      // rates so pills and the estimate request use $875/wk instead of monthly ÷ 3.
      this.inventoryService
        .getAttachments(this.machine.id)
        .pipe(catchError(() => of([])))
        .subscribe(associations => {
          applyAssociationWeeklyPrices(this.machine.relatedAttachments, associations);
        });
    }
  }

  startDateSelected(date: NgbDate) {
    this.startDate = date;
  }

  endDateSelected(date: NgbDate) {
    this.endDate = date;
  }

  durationSelected(duration: number) {
    // Always coerce — older emitters sent strings and `date + duration` would concatenate.
    this.duration = Number(duration) || 0;
  }

  /**
   * Resolve the end date to seed the end-date picker when editing an existing
   * cart item (or continuing from the last item in the cart). Prefers the
   * backend-provided end date; falls back to begin + (duration - 1) days so the
   * picker always has a sensible value even for older carts. Duration is an
   * inclusive day count (both start and end dates count).
   */
  private resolveEndDate(beginDate: string, endDate: string, duration: number): NgbDate {
    if (endDate) {
      return DateRange.createNgbDateFromString(endDate);
    }

    const days = Number(duration) || 0;
    if (beginDate && days > 0) {
      const begin = DateRange.createDateFromString(beginDate);
      begin.setDate(begin.getDate() + days - 1);
      return new NgbDate(begin.getFullYear(), begin.getMonth() + 1, begin.getDate());
    }

    return null;
  }

  zipcodeChange(zipcode: string) {
    this.zipcode = zipcode;
    if (this.cartItem) {
      // since we are changing zip code we need to wipe out joblocation so that the api create a new estimate
      this.cartItem.jobLocation = null;
    }
  }

  locationSelected(location: Location) {
    this.deliveryAddress = location;
    this.invalidDeliveryAddress = false;
    this.zipcode = this.deliveryPostalCode;
    if (this.cartItem) {
      this.cartItem.jobLocation = location;
    }
  }

  get deliveryPostalCode(): string {
    return this.deliveryAddress?.address?.postalCode || "";
  }

  /** Re-estimate when the renter destination is new or the street/coords changed. */
  shouldEstimateTransportation(): boolean {
    if (!this.hasValidDeliveryLocation(this.deliveryAddress)) {
      return false;
    }
    const previous = this.cartItem?.jobLocation;
    if (this.hasValidDeliveryLocation(previous) && this.sameDestination(previous, this.deliveryAddress)) {
      return false;
    }
    if (
      !this.hasValidDeliveryLocation(previous) &&
      this.cartItem?.jobZipCode &&
      this.deliveryPostalCode === this.cartItem.jobZipCode &&
      !this.deliveryAddress?.address?.address1 &&
      !this.hasValidJobsiteCoordinates(this.deliveryAddress)
    ) {
      return false;
    }
    return true;
  }

  private sameDestination(left: Location, right: Location): boolean {
    const leftStreet = (left?.address?.address1 || "").trim().toLowerCase();
    const rightStreet = (right?.address?.address1 || "").trim().toLowerCase();
    const leftPostal = left?.address?.postalCode || "";
    const rightPostal = right?.address?.postalCode || "";
    if (leftStreet || rightStreet) {
      return leftStreet === rightStreet && leftPostal === rightPostal;
    }
    return leftPostal === rightPostal && left?.latitude === right?.latitude && left?.longitude === right?.longitude;
  }

  /** Header search-location dropdown — used only to center the coordinates map. */
  get searchMapLocation(): Location {
    return this.resolveSearchDropdownLocation();
  }

  hasValidDeliveryLocation(location: Location): boolean {
    if (!location) {
      return false;
    }
    return !!location.address?.postalCode || this.hasValidJobsiteCoordinates(location);
  }

  resolveSearchDropdownLocation(): Location {
    const live = this.searchResultsService?.searchRequest?.location?.location;
    if (this.hasMapCenter(live)) {
      return live;
    }
    const stored = this.localStorageService?.get("current-location");
    if (this.hasMapCenter(stored)) {
      return stored;
    }
    return null;
  }

  private hasMapCenter(location: Location): boolean {
    if (!location || location.address?.address1 === "Current Location") {
      return false;
    }
    const latitude = Number(location.latitude);
    const longitude = Number(location.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && !(latitude === 1 && longitude === 1);
  }

  private hasValidJobsiteCoordinates(location: Location): boolean {
    const latitude = Number(location?.latitude);
    const longitude = Number(location?.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }

  private locationFromPostal(postal: string): Location {
    const location = new Location();
    location.address = new Address();
    location.address.postalCode = postal;
    return location;
  }

  private resolveEditPrefillLocation(cartItem: CartItem, cart: Cart): Location {
    if (this.hasValidDeliveryLocation(cartItem?.jobLocation)) {
      return cartItem.jobLocation;
    }
    if (this.hasValidDeliveryLocation(cart?.transportationInfo?.deliveryAddress)) {
      return cart.transportationInfo.deliveryAddress;
    }
    if (cartItem?.jobZipCode && cartItem.jobZipCode !== "00000") {
      return this.locationFromPostal(cartItem.jobZipCode);
    }
    return null;
  }

  private resolveNewAddPrefillLocation(lastItem: CartItem, cart: Cart): Location {
    if (this.hasValidDeliveryLocation(cart?.transportationInfo?.deliveryAddress)) {
      return cart.transportationInfo.deliveryAddress;
    }
    if (this.hasValidDeliveryLocation(lastItem?.jobLocation)) {
      return lastItem.jobLocation;
    }
    if (lastItem?.jobZipCode && lastItem.jobZipCode !== "00000") {
      return this.locationFromPostal(lastItem.jobZipCode);
    }
    return null;
  }

  rpoInterestedSelected(rpoInterested: boolean) {
    this.rpoInterested = rpoInterested;
  }

  deliveryOptionSelected(deliveryOption: string) {
    this.deliveryOption = deliveryOption;
    // Immediately update cart's delivery option if cart already exists
    if (this.cartService.cart?.id) {
      this.cartService.updateDeliveryOption(deliveryOption).subscribe();
    }
  }

  applyCoupon() {
    if (!this.couponCode?.trim()) return;

    this.couponLoading = true;
    this.couponError = "";

    this.couponService.validateCoupon(this.couponCode.trim(), this.user?.profile?.account_id).subscribe({
      next: response => {
        this.couponLoading = false;

        if (!response.isValid) {
          this.couponError = response.message || "Invalid coupon code";
          return;
        }

        const couponRollback = { applied: this.appliedCouponCode, validated: this.validatedCouponCode ?? "" };

        this.appliedCouponCode = response.coupon?.code || this.couponCode.trim();
        this.validatedCouponCode = this.appliedCouponCode;
        this.couponCode = "";
        this.couponError = "";

        // Re-fetch estimate with coupon code so backend applies discount to totals.
        if (this.cartItem) {
          this.refreshEstimateWithCoupon(couponRollback);
        }
      },
      error: err => {
        this.couponLoading = false;
        this.logService.warn("MachineCardAddToCartComponent", err);
        this.toastrService.error("Could not validate coupon. Please try again.");
      },
    });
  }

  removeCoupon() {
    const couponRollback = { applied: this.appliedCouponCode, validated: this.validatedCouponCode ?? "" };

    this.appliedCouponCode = "";
    this.validatedCouponCode = "";
    this.couponCode = "";
    this.couponError = "";

    // Re-fetch estimate without coupon code to remove any discount.
    if (this.cartItem) {
      this.refreshEstimateWithCoupon(couponRollback);
    }
  }

  private refreshEstimateWithCoupon(couponRollback?: { applied: string; validated: string }) {
    const existingCartItemId = this.cartItem?.id;
    const cartItemEstimate = { ...this.cartItem } as CartItem;
    this.cartService.getCartItemEstimate(cartItemEstimate, this.validatedCouponCode || "").subscribe({
      next: result => {
        // Preserve transportation estimate because this call is only for coupon-adjusted totals.
        result.transportationEstimateSummary = this.cartItem.transportationEstimateSummary;
        this.cartItem = result;
        // Estimate returns a new line id — keep the existing one so updateCartItem can find the row.
        if (existingCartItemId) {
          this.cartItem.id = existingCartItemId;
        }
      },
      error: err => {
        this.logService.warn("MachineCardAddToCartComponent", err);
        this.toastrService.error("Could not refresh pricing after coupon change. Please try again.");
        if (couponRollback) {
          this.appliedCouponCode = couponRollback.applied;
          this.validatedCouponCode = couponRollback.validated;
        }
      },
    });
  }

  startDateAndDurationCompleted() {
    this.loading = true;
    this.getCartItemEstimate();
  }

  private getCartItemEstimate() {
    // check if start date and job duration is null. if so then just send the request with one month
    // Use the first thumbnail from the machine - this handles both actual photos and stock catalog photos
    const firstThumbnail = this.machine.thumbnails?.length > 0 ? this.machine.thumbnails[0] : this.machine.images?.[0];
    // Preserve across estimate — the estimate API mints a new CartItem id.
    const existingCartItemId = this.cartItem?.id;

    const selectedAttachments = this.machine.relatedAttachments
      .filter(d => d.selected && d.standard)
      .concat(this.machine.relatedAttachments.filter(d => d.selected && !d.standard))
      .map(attachment => this.ensureAttachmentCartFields(attachment));

    const postal = this.deliveryPostalCode;
    let cartItemEstimate: CartItem = new CartItem(
      DateRange.createDateFromNgbDate(this.startDate),
      this.duration,
      this.machine,
      selectedAttachments,
      this.rpoInterested,
      postal,
      firstThumbnail,
    );
    cartItemEstimate.jobLocation = this.deliveryAddress;
    cartItemEstimate.charges = [new Charge(this.machine.rentalRate, "Rent", "Monthly")];

    let transpoCost = of({
      min: 0,
      max: 0,
      average: 0,
      distance: 0,
      explanation: "",
    });

    if (this.shouldEstimateTransportation()) {
      transpoCost = this.openAIService.estimateTransportationCost(this.machine, this.deliveryAddress);
    }

    const couponForEstimate = this.validatedCouponCode ?? this.cartService.cart?.couponCode ?? "";

    // Get cart item estimate and transportation cost estimate in parallel
    forkJoin({
      cartItem: this.cartService.getCartItemEstimate(cartItemEstimate, couponForEstimate),
      transportationCost: transpoCost,
    }).subscribe({
      next: result => {
        this.cartItem = result.cartItem;
        // Estimate returns a new line id — keep the existing one so updateCartItem can find the row.
        if (existingCartItemId) {
          this.cartItem.id = existingCartItemId;
        }

        if (this.cartItem.jobZipCode == "00000") {
          this.toastrService.error("Invalid Delivery Address.");
          this.invalidDeliveryAddress = true;
          this.loading = false;
          return;
        }

        this.invalidDeliveryAddress = false;
        // Funnel: user clicked "See Total" after picking date/duration/ZIP and the estimate
        // succeeded — this is the moment the user commits to adding to cart, so fire
        // GA `add_to_cart` + FB `AddToCart`. Skip for edit-in-cart and edit-in-checkout
        // flows (which use the "Edit" / "Save" button labels, not "See Total").
        if (!this.machineInCart && !this.inCheckout) {
          const cartForEvent = { items: [this.cartItem] } as Cart;
          this.logService.sendGoogleEcommerceEvent("add_to_cart", cartForEvent);
          this.logService.sendFacebookCartEvent("AddToCart", cartForEvent, this.user);
        }
        // Add transportation cost to the cart item only if we got a valid response
        if (result.transportationCost && result.transportationCost.average > 0) {
          // Add the average cost as the main transportation charge
          this.cartItem.transportationEstimateSummary = {
            ...this.cartItem.transportationEstimateSummary,
            estimatedCost: result.transportationCost.average,
            internalNotes: `$${result.transportationCost.min.toLocaleString()} - $${result.transportationCost.max.toLocaleString()}; ${result.transportationCost.explanation
              }`,
            distance: {
              value: result.transportationCost.distance.toString(),
              unit: {
                fullName: "Miles",
                abbreviations: ["mls"],
                system: "Standard",
              },
              system: "Standard",
            },
          };
          this.cachedTransportationEstimateSummary = this.cartItem.transportationEstimateSummary;
        } else {
          this.cartItem.transportationEstimateSummary = this.cachedTransportationEstimateSummary;
        }

        // if we are in checkout lets immediately update the cart item and close the modal. do not go to the review details
        if (this.inCheckout) {
          this.cartService.updateCartItem(this.cartItem, false).subscribe({
            next: () => this.activeModal.close(),
            error: err => {
              this.logService.warn("MachineCardAddToCartComponent", err);
              this.loading = false;
              this.toastrService.error("Failed to save cart item. Please try again.");
            },
          });
          return;
        }

        // Skip the legacy "Rental details" review step. Now that the estimate is ready,
        // perform the add (or update) immediately and let saveCouponAndCheckout navigate
        // the user to the new /cart page.
        this.addToCartCompleted();
      },
      error: err => {
        this.logService.warn("MachineCardAddToCartComponent", err);
        this.loading = false;
        this.toastrService.error("Failed to get estimate. Please try again.");
      },
    });
  }

  addToCartCompleted() {
    this.loading = true;
    if (this.machineInCart) {
      // Updating existing cart item
      this.cartService.updateCartItem(this.cartItem, false).subscribe({
        next: () => this.saveCouponAndCheckout(),
        error: err => {
          this.logService.warn("MachineCardAddToCartComponent", err);
          this.loading = false;
          this.toastrService.error("Failed to save cart item. Please try again.");
        },
      });
    } else {
      this.performNewAddToCart();
    }
  }

  /**
   * Cart estimate/create requires RelatedItem fields the machine index does not carry:
   * `childItemId` (attachment inventory id) and `accountId` (partition). Enrich from the
   * attachments index when available; fall back to the parent machine account.
   */
  private ensureAttachmentCartFields(attachment: Attachment): Attachment {
    attachment.childItemId = attachment.childItemId || attachment.inventoryItemId;
    attachment.accountId = attachment.accountId || this.machine.accountId;
    if (attachment.price == null) {
      attachment.price = attachment.cost;
    }
    return attachment;
  }

  /** New add: add line item then patch delivery, then navigate to /cart. */
  private performNewAddToCart() {
    this.cartService.cart.deliveryOption = this.deliveryOption;
    // Always show the cart-service toast since the modal closes immediately and the user
    // is taken to /cart — the toast provides the confirmation that previously came from
    // the (now removed) "View cart / Continue shopping" panel.
    this.cartService.addItemToCart(this.cartItem, true).subscribe(
      () => this.saveCouponAndCheckout(),
      () => {
        this.loading = false;
        this.toastrService.error("Could not add to cart. Please try again.");
      },
    );
  }

  private saveCouponAndCheckout() {
    if (!this.cartService.cart?.id) {
      if (this.navigateToCartAfterSave) {
        this.viewCart();
      } else {
        this.checkout();
      }
      return;
    }

    const transportationInfo = this.cartService.cart.transportationInfo || new TransportationInfo();
    transportationInfo.deliveryAddress = this.deliveryAddress;

    const patchOperations: { op: string; path: string; value: any }[] = [
      { op: "replace", path: "/deliveryOption", value: this.deliveryOption },
      { op: "add", path: "/couponCode", value: this.validatedCouponCode || "" },
      { op: "replace", path: "/transportationInfo", value: transportationInfo },
    ];

    this.cartService
      .jsonPatchWithPartitionKey(
        this.cartService.cart.id,
        patchOperations,
        this.cartService.cart.partitionKey || this.user?.profile?.account_id
      )
      .subscribe({
        next: () => {
          // Update local cart state so modal/cart views reflect saved values.
          this.cartService.cart.deliveryOption = this.deliveryOption;
          this.cartService.cart.couponCode = this.validatedCouponCode || "";
          this.cartService.cart.transportationInfo = transportationInfo;
          if (this.navigateToCartAfterSave) {
            this.viewCart();
          } else {
            this.checkout();
          }
        },
        error: err => {
          this.logService.warn("MachineCardAddToCartComponent", err);
          this.loading = false;
          this.toastrService.error("Could not update cart. Please try again.");
        },
      });
  }

  viewCart() {
    this.logService.trackEvent("add-to-cart-view-cart-click");
    this.router.navigate(["/cart"]);
    this.activeModal.close();
  }

  checkout() {
    this.logService.trackEvent("add-to-cart-checkout-click");
    this.router.navigate(["checkout"]);
    this.activeModal.close();
  }

  onPrevious() {
    this.counter--;
  }

  /**
   * Returns the subset of `attachments` whose status is currently `Available` or `Onboarding`,
   * plus any attachment whose `inventoryItemId` is referenced by `cartItemAttachmentIds`.
   *
   * The cart-item fallback ensures the edit flow can still display (and let the user remove)
   * attachments that the user previously selected but which have since become unavailable.
   *
   * Pure function — exposed `static` for testability without bootstrapping the full component.
   */
  static filterAttachmentsByAvailability(attachments: Attachment[], cartItemAttachmentIds: string[]): Attachment[] {
    const cartIds = new Set(cartItemAttachmentIds || []);
    return (attachments || []).filter(
      a => a.status === "Available" || a.status === "Onboarding" || cartIds.has(a.inventoryItemId),
    );
  }
}
